import { Link } from 'react-router-dom';
import { Zap, Loader2, ChevronLeft, ChevronRight, CheckCircle, Dumbbell } from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';

export const PersonalizedPlanSection = ({
  myPlan, generatingPlan, planWeek, setPlanWeek, onGenerate, onRegenerate, onToggleDayComplete,
}) => (
  <div className="mt-6 pt-6 border-t border-border">
    <div className="flex items-center gap-2 mb-4">
      <Zap className="h-5 w-5 text-primary" />
      <h3 className="font-heading text-lg text-white uppercase tracking-wider">Your Personalized Plan</h3>
    </div>

    {!myPlan ? (
      <div className="text-center py-4">
        <p className="text-muted-foreground text-sm mb-4">
          Get an AI-generated workout plan tailored to your fitness level and goals
        </p>
        <Button
          onClick={onGenerate}
          disabled={generatingPlan}
          className="bg-primary text-black w-full"
          data-testid="generate-plan-btn"
        >
          {generatingPlan ? (
            <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating Your Plan...</>
          ) : (
            <><Zap className="h-4 w-4 mr-2" /> Generate My Plan</>
          )}
        </Button>
      </div>
    ) : (
      <div>
        {(myPlan.progress > 0 || myPlan.completed_days > 0) && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-muted-foreground text-xs uppercase">Progress</span>
              <span className="text-primary text-xs font-heading">{myPlan.completed_days || 0}/{myPlan.total_workout_days || '?'} days</span>
            </div>
            <Progress value={myPlan.progress || 0} className="h-2 bg-black" />
          </div>
        )}

        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setPlanWeek(w => Math.max(1, w - 1))}
            disabled={planWeek <= 1}
            className="text-white disabled:text-muted-foreground p-1"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <span className="font-heading text-primary uppercase tracking-wider text-sm">
            Week {planWeek} — {myPlan.weeks?.[planWeek - 1]?.focus || ''}
          </span>
          <button
            onClick={() => setPlanWeek(w => Math.min(myPlan.duration_weeks || 8, w + 1))}
            disabled={planWeek >= (myPlan.duration_weeks || 8)}
            className="text-white disabled:text-muted-foreground p-1"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Days */}
        <div className="space-y-2">
          {myPlan.weeks?.[planWeek - 1]?.days?.map((day, i) => (
            <div key={i} className={`flex items-center gap-3 p-3 rounded border transition-colors ${
              day.completed
                ? 'border-green-500/30 bg-green-500/5'
                : day.type === 'rest'
                  ? 'border-border bg-card/50'
                  : 'border-primary/20 bg-card'
            }`}>
              <button
                onClick={() => onToggleDayComplete(i)}
                className={`h-6 w-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  day.completed ? 'bg-green-500 border-green-500' : 'border-muted-foreground/40 hover:border-primary'
                }`}
                data-testid={`complete-day-${planWeek}-${i}`}
              >
                {day.completed && <CheckCircle className="h-4 w-4 text-white" />}
              </button>

              <span className="font-heading text-xs uppercase w-8 text-muted-foreground">
                {day.day?.slice(0, 3)}
              </span>
              {day.type === 'rest' ? (
                <div className="flex-1">
                  <p className={`text-sm italic ${day.completed ? 'text-green-400 line-through' : 'text-muted-foreground'}`}>
                    {day.notes || 'Rest Day'}
                  </p>
                </div>
              ) : (
                <Link to={day.workout_id ? `/workouts/${day.workout_id}` : '#'} className="flex-1">
                  <p className={`text-sm font-medium ${day.completed ? 'text-green-400 line-through' : 'text-white'}`}>
                    {day.workout_title || 'Workout'}
                  </p>
                  {day.notes && <p className="text-muted-foreground text-xs">{day.notes}</p>}
                </Link>
              )}
              {day.type === 'workout' && !day.completed && (
                <Dumbbell className="h-4 w-4 text-primary flex-shrink-0" />
              )}
            </div>
          ))}
        </div>

        <Button
          onClick={onRegenerate}
          disabled={generatingPlan}
          variant="outline"
          className="w-full mt-4 border-border text-muted-foreground hover:text-white"
          data-testid="regenerate-plan-btn"
        >
          {generatingPlan ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Regenerating...</> : 'Regenerate Plan'}
        </Button>
      </div>
    )}
  </div>
);
